import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import {
  ASSET_TYPES,
  buildQueryFilter,
  buildQueryFilterFromClauses,
  buildTreeFilterClauses,
  createAggregationParams,
  createSearchParams,
  type ElasticsearchQueryFilter,
  INDICES,
} from '@/shared/api/openmetadata';

import type { OpenMetadataResponse } from '@/entities/search';

export const fetchInitialTree = async (): Promise<OpenMetadataResponse> => {
  const params = createAggregationParams(INDICES.DATA_ASSET);

  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params,
  });

  return response.data;
};

export const fetchEntityTypeChildren = async (params: {
  entityType: string;
  index: string;
  useAggregation: boolean;
  entityTypes?: string[];
  serviceType?: string;
  serviceName?: string;
  databaseName?: string;
  schemaName?: string;
}): Promise<OpenMetadataResponse> => {
  const { entityType, index, useAggregation, entityTypes, serviceType, serviceName, databaseName, schemaName } = params;

  if (useAggregation) {
    const mustClauses = buildTreeFilterClauses({ serviceType, serviceName, databaseName, schemaName });
    const queryFilter = buildQueryFilterFromClauses(mustClauses);

    const queryParams = createAggregationParams(index, queryFilter);

    const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
      params: queryParams,
    });

    return response.data;
  }

  const mustClauses: Array<Record<string, unknown>> = [];

  if (entityType === ASSET_TYPES.TABLE) {
    mustClauses.push({
      term: { 'entityType.keyword': ASSET_TYPES.TABLE },
    });
  } else if (entityTypes && entityTypes.length > 0) {
    const shouldClauses = entityTypes.map((type) => ({
      term: { 'entityType.keyword': type },
    }));

    mustClauses.push({
      bool: {
        should: shouldClauses,
        minimum_should_match: 1,
      },
    });
  }

  mustClauses.push(...buildTreeFilterClauses({ serviceType, serviceName, databaseName, schemaName }));

  const queryFilter = buildQueryFilterFromClauses(mustClauses);

  const queryParams = createSearchParams(index, 1000, queryFilter, 'columns');

  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params: queryParams,
  });

  return response.data;
};

export const fetchTreeChildren = async (
  nodeType: 'entityType' | 'serviceType' | 'service' | 'database' | 'schema',
  params: {
    index?: string;
    entityType?: string;
    serviceType?: string;
    serviceName?: string;
    databaseName?: string;
    schemaName?: string;
  }
): Promise<OpenMetadataResponse> => {
  let index = params.index || INDICES.DATA_ASSET;
  let queryFilter: ElasticsearchQueryFilter = { query: { bool: {} } };

  switch (nodeType) {
    case 'entityType':
      queryFilter = buildQueryFilter([]);
      break;

    case 'serviceType':
      if (params.serviceType) {
        const mustClauses = buildTreeFilterClauses({
          serviceType: params.serviceType,
        });
        queryFilter = buildQueryFilterFromClauses(mustClauses);
      }
      break;

    case 'service':
      index = INDICES.DATABASE;
      if (params.serviceType && params.serviceName) {
        const mustClauses = buildTreeFilterClauses({
          serviceType: params.serviceType,
          serviceName: params.serviceName,
        });
        queryFilter = buildQueryFilterFromClauses(mustClauses);
      }
      break;

    case 'database':
      index = INDICES.DATABASE_SCHEMA;
      if (params.serviceName && params.databaseName) {
        const mustClauses = buildTreeFilterClauses({
          serviceType: params.serviceType,
          serviceName: params.serviceName,
          databaseName: params.databaseName,
        });
        queryFilter = buildQueryFilterFromClauses(mustClauses);
      }
      break;

    case 'schema':
      index = INDICES.TABLE;
      if (params.serviceName && params.databaseName && params.schemaName) {
        const mustClauses = buildTreeFilterClauses({
          serviceType: params.serviceType,
          serviceName: params.serviceName,
          databaseName: params.databaseName,
          schemaName: params.schemaName,
        });
        queryFilter = buildQueryFilterFromClauses(mustClauses);
      }
      break;
  }

  const queryParams = createAggregationParams(index, queryFilter);

  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params: queryParams,
  });

  return response.data;
};
